<template>
    <article class="quote-form-container" :style="style">
        <div class="quote-form">
            <div
                class="quote-form__header"
                :class="{
                    'quote-form__header--mobile-background': props.enableMobileBackground,
                }"
            >
                <WatsonAtomDynamicText
                    v-if="props.heading"
                    :text="props.heading"
                    :typography="props.typography.heading"
                    class="quote-form__heading"
                />

                <WatsonAtomDynamicText
                    v-if="props.nudgeText"
                    :text="props.nudgeText"
                    :typography="props.typography.nudgeText"
                    class="quote-form__nudge-text"
                />
            </div>
            <div class="quote-form__content">
                <ul
                    v-if="items.length"
                    class="quote-form__list"
                >
                    <li
                        v-for="(item, index) in items"
                        :key="index"
                        class="quote-form__item"
                        :class="{
                            'quote-form__item--border': props.enableAnswerBorder,
                        }"
                    >
                        <button
                            class="quote-form__item-container"
                            @click="buttonClick(item.answer)"
                        >
                            <div
                                v-if="item.image"
                                class="quote-form__item-image-wrapper"
                            >
                                <WatsonAtomImage
                                    v-if="item.image"
                                    :content="item.image"
                                    class="quote-form__item-image"
                                />
                            </div>

                            <ds-atom-button
                                v-if="item.text"
                                component="span"
                                full-width
                                size="small"
                            >
                                <template #text="{ classes }">
                                    <WatsonAtomDynamicText
                                        :text="item.text"
                                        :class="classes"
                                        :typography="props.typography.button"
                                    />
                                </template>
                            </ds-atom-button>
                        </button>
                    </li>
                </ul>
                <div class="quote-form__banner">
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        class="quote-form__item-text"
                        v-html="props.bannerText"
                    ></div>
                    <!-- eslint-enable vue/no-v-html -->
                </div>
            </div>
        </div>
    </article>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useQuoteForm } from '../../js/stores/quote-form';
import { handleZoeRedirect } from '../../../silver-blaze/js/utils/zoe-redirect';
import { DsAtomButton } from '@leads.io/design-system-component-library/marketplaces';
import WatsonAtomDynamicText from '@atoms/dynamic-text/watson-atom-dynamic-text.vue';
import WatsonAtomImage from '@atoms/image/watson-atom-image.vue';

const props = defineProps({
    items: {
        type: Array,
        required: true,
    },
    zoeRedirect: {
        type: String,
        required: true,
    },
    heading: {
        type: String,
        required: true,
    },
    nudgeText: {
        type: String,
        required: true,
    },
    enableMobileBackground: {
        type: Boolean,
        required: false,
        default: false,
    },
    enableAnswerBorder: {
        type: Boolean,
        required: false,
        default: false,
    },
    bannerText: {
        type: String,
        required: true,
    },
    uspsTurnOff: {
        type: Boolean,
        required: false,
        default: false,
    },
    background: {
        type: String,
        required: false,
        default: null,
    },
    headerBackground: {
        type: String,
        required: false,
        default: null,
    },
    headerTextColorDesktop: {
        type: String,
        required: false,
        default: null,
    },
    headerTextColor: {
        type: String,
        required: false,
        default: null,
    },
    answerBorder: {
        type: String,
        required: false,
        default: null,
    },
    answerActiveBorder: {
        type: String,
        required: false,
        default: null,
    },
    bannerBackground: {
        type: String,
        required: false,
        default: null,
    },
    bannerTextColor: {
        type: String,
        required: false,
        default: null,
    },
    uspsData: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    typography: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const quoteForm = useQuoteForm();
const { aaci, aacri } = storeToRefs(quoteForm);

const buttonClick = answer => {
    handleZoeRedirect(props.zoeRedirect, answer, aacri, aaci);
};

const style = computed(() => ({
    '--background': props.background || 'initial',
    '--header-background': props.headerBackground || 'initial',
    '--header-text-color-desktop': props.headerTextColorDesktop || 'initial',
    '--header-text-color': props.headerTextColor || 'initial',
    '--answer-border': props.answerBorder || 'initial',
    '--answer-active-border': props.answerActiveBorder || 'initial',
    '--banner-background': props.bannerBackground || 'initial',
    '--banner-text-color': props.bannerTextColor || 'initial',
}));
</script>

<style lang="scss">
@use 'watson-theme-quote-form';
</style>
